// SDK boot-loader wiring for dice (SC-61728) — the same per-launch model Plinko ships (SC-59810):
//   - `?loader=full`  the branded CoinPoker splash, held at least FULL_SPLASH_MIN_VISIBLE_MS so it reads as a
//                     loader and not a flash;
//   - `?loader=veil`  (the dice DEFAULT) a plain cover that cross-fades to the board on the SDK's own timing;
//   - `?loader=none`  nothing (the host reveals).
// Anything unrecognised falls back to the game default, never to the branded splash.
//
// The loader covers the board, but the vendored game's hotkeys (Space = Roll) fire through any overlay, so the
// operator also needs to know WHEN the board is actually visible: the SDK mounts `[data-loader-root]` while it
// covers the board (loading, and its terminal error/retry surface) and removes it on reveal.

export type LoaderMode = "full" | "veil" | "none";

export const GAME_DEFAULT_LOADER: LoaderMode = "veil";

/** The branded splash's minimum visible dwell (ms) — only ever passed for `full`. */
export const FULL_SPLASH_MIN_VISIBLE_MS = 2000;

const LOADER_ROOT_SELECTOR = "[data-loader-root]";

export function readLoaderMode(search: string): LoaderMode {
  const v = new URLSearchParams(search).get("loader");
  return v === "full" || v === "veil" || v === "none" ? v : GAME_DEFAULT_LOADER;
}

export interface LoaderConfig {
  readonly loader: LoaderMode;
  /** The loader's Retry. The SDK never reloads or navigates by itself; the game reloads (REAL resumes). */
  readonly onLoaderRetry: () => void;
  readonly loaderMinVisibleMs?: number;
}

export function loaderConfig(mode: LoaderMode, reload: () => void): LoaderConfig {
  return {
    loader: mode,
    onLoaderRetry: () => reload(),
    ...(mode === "full" ? { loaderMinVisibleMs: FULL_SPLASH_MIN_VISIBLE_MS } : {}),
  };
}

/** True while the SDK loader (or its error/retry surface) covers the board. */
export function boardCovered(doc: Document): boolean {
  return doc.querySelector(LOADER_ROOT_SELECTOR) !== null;
}

/** Resolves once no SDK loader covers the board — immediately if none is mounted. Never rejects; it simply keeps
 *  waiting while the loader stays up (e.g. on its terminal error surface, where only Retry/reload leaves). Watches
 *  only the root's parent (childList), so the loader's own per-frame readout never wakes it. */
export function whenBoardUncovered(doc: Document): Promise<void> {
  const root = doc.querySelector(LOADER_ROOT_SELECTOR);
  const parent = root?.parentNode;
  if (!root || !parent) return Promise.resolve();
  return new Promise((resolve) => {
    const observer = new MutationObserver(() => {
      if (root.isConnected) return;
      observer.disconnect();
      resolve();
    });
    observer.observe(parent, { childList: true });
  });
}

const GUARDED_KEY_EVENTS = ["keydown", "keypress", "keyup"] as const;

const stopAtLoader = (e: Event): void => e.stopPropagation();
const guardedRoots = new WeakSet<Element>();

/** Keys whose browser default must survive while the board is covered: Tab (focus moves — onto the loader's Retry) and
 *  browser shortcuts (Ctrl/Cmd/Alt combinations, F-keys: F5 / Ctrl+R reload). The game still never sees them. */
function keepsBrowserDefault(e: Event): boolean {
  if (!(e instanceof KeyboardEvent)) return false;
  return e.key === "Tab" || e.ctrlKey || e.metaKey || e.altKey || /^F\d{1,2}$/.test(e.key);
}

/** While the SDK loader covers the board, keyboard input never reaches the GAME: the vendored game rolls on Space from a
 *  document-level listener, and the loader root only absorbs POINTER input (it covers the viewport, on top). A window
 *  capture-phase listener runs before any game handler. It must not trap the PLAYER, whose only way out of the loader's
 *  terminal error surface is its Retry button or a reload:
 *   - keys aimed at the loader itself (its Retry) pass untouched;
 *   - Tab and browser shortcuts keep their default (only the game is kept from seeing them);
 *   - the board (#app) is `inert` meanwhile, so Tab skips its hidden controls and lands on the loader.
 *  It removes itself once the board is uncovered, and keeps guarding behind the terminal error surface. */
export function blockKeysWhileCovered(doc: Document): void {
  const win = doc.defaultView;
  if (!win || !boardCovered(doc)) return;
  const app = doc.getElementById("app");
  app?.setAttribute("inert", "");
  const swallow = (e: Event): void => {
    const root = doc.querySelector(LOADER_ROOT_SELECTOR);
    if (!root) return;
    if (e.target instanceof Node && root.contains(e.target)) {
      // The loader's own controls (its Retry): the key reaches them untouched, so their native activation works, and a
      // bubble-phase stop on the loader root keeps it from the game's document-level handlers — the game's Space
      // handler would preventDefault() it (and call its CTA), cancelling the button's activation.
      if (!guardedRoots.has(root)) {
        for (const type of GUARDED_KEY_EVENTS) root.addEventListener(type, stopAtLoader);
        guardedRoots.add(root);
      }
      return;
    }
    e.stopImmediatePropagation();
    if (!keepsBrowserDefault(e)) e.preventDefault();
  };
  for (const type of GUARDED_KEY_EVENTS) win.addEventListener(type, swallow, true);
  void whenBoardUncovered(doc).then(() => {
    for (const type of GUARDED_KEY_EVENTS) win.removeEventListener(type, swallow, true);
    app?.removeAttribute("inert");
  });
}

/** Lifts the static boot cover the generated index.html puts on <html> (`data-rgs-boot`, see
 *  scripts/vendor-sync.mjs): call once the SDK loader has taken over the screen, or to show a block message. */
export function revealBoot(doc: Document): void {
  doc.documentElement.removeAttribute("data-rgs-boot");
}
